"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import "./Shop.module.css";
import { Link } from "@/components/common/Link";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import Catalog from "./sections/Catalog";
import PackPromo from "./sections/PackPromo";
import { useAccessGate } from "@/hooks/useAccessGate";
const SHOP_VIALS_IMAGE = "https://tektideslabs.com/wp-content/uploads/2026/10/Premium-Research-Vial-Collection-1.png";
export default function ShopPage() {
  const reduceMotion = useReducedMotion();
  const {
    accessGranted
  } = useAccessGate();
  return <div className="min-h-screen bg-[#f8fafb] text-[#0a1218]">
      <Header />

      <main id="main-content">
        {/* SHOP HERO */}
        <section aria-labelledby="shop-heading" className="bg-[#f8fafb] px-4 pt-12 pb-8 sm:px-6 lg:px-8 lg:pt-16">
          <div className="mx-auto max-w-[1440px]">
            <div className={`grid items-center gap-10 lg:gap-12 ${accessGranted ? "lg:grid-cols-[0.85fr_1.15fr]" : ""}`}>
              {/* LEFT — SHOP TEXT */}
              <motion.div initial={reduceMotion ? false : {
              opacity: 0,
              y: 18
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: reduceMotion ? 0 : 0.65
            }} className="relative z-10">
                <div className="mb-7 flex items-center gap-3">
                  <span className="h-px w-8 bg-[#159fdf]" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#1479a7]">
                    The TEK TIDES Collection
                  </span>
                </div>

                <h1 id="shop-heading" className="max-w-[560px] text-[clamp(3.3rem,5.5vw,6rem)] font-semibold leading-[0.94] tracking-[-0.065em] text-[#0a1218]">
                  For the
                  <br />
                  research
                  <br />
                  ahead.
                </h1>

                <p className="mt-7 max-w-[460px] text-sm leading-7 text-[#56636a] sm:text-base">
                  Research-use products with clear product information
                  and documentation available upon request.
                </p>

                <Link to="#shop-catalog" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#087db3] transition-colors hover:text-[#066d9d]">
                  Explore the collection
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </motion.div>

              {/* RIGHT — UPDATED TRANSPARENT VIAL IMAGE */}
              {accessGranted && <motion.div initial={reduceMotion ? false : {
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: reduceMotion ? 0 : 0.8,
              delay: reduceMotion ? 0 : 0.1
            }} className="relative flex min-h-[220px] min-w-0 items-center justify-center sm:min-h-[290px] lg:min-h-[400px]">
                  <img src={SHOP_VIALS_IMAGE} alt="TEK TIDES LABS research vials: GLP-3 RT 30 MG, Tesamorelin 10 MG, GLP-2 TR 60 MG, BPC-157 plus TB-500, and MOTS-C 40 MG" decoding="async" className="block h-auto w-full max-w-[800px] object-contain" />
                </motion.div>}
            </div>

            {/* BOTTOM HERO DETAIL */}
            <div className="mt-8 flex items-center justify-end border-b border-[#dbe6eb] pb-5">
              <span className="text-[9px] font-medium uppercase tracking-[0.16em] text-[#87969d]">
                Research Use Only
              </span>
            </div>
          </div>
        </section>

        {/* WELCOME25 OFFER */}
        {accessGranted && <section className="bg-[#f8fafb] px-4 py-8 sm:px-6 md:py-10 lg:px-8" aria-labelledby="welcome-offer-heading">
            <div className="mx-auto max-w-[1440px]">
              <div className="relative overflow-hidden rounded-[30px] border border-[#dbe6eb] bg-[#eef5f8]">
                {/* Subtle blue atmosphere */}
                <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-32 h-[360px] w-[360px] rounded-full bg-[#159fdf]/10 blur-[100px]" />

                <div aria-hidden="true" className="pointer-events-none absolute -bottom-36 left-[20%] h-[300px] w-[420px] rounded-full bg-[#087db3]/[0.05] blur-[110px]" />

                <div className="relative z-10 flex flex-col gap-10 px-7 py-9 sm:px-10 sm:py-11 lg:flex-row lg:items-center lg:justify-between lg:px-14 lg:py-12">
                  {/* LEFT — OFFER */}
                  <div className="max-w-[680px]">
                    <div className="mb-5 flex items-center gap-3">
                      <span aria-hidden="true" className="h-px w-8 bg-[#159fdf]" />

                      <p className="text-[10px] font-semibold uppercase tracking-[0.23em] text-[#1479a7]">
                        New Customer Offer
                      </p>
                    </div>

                    <h2 id="welcome-offer-heading" className="text-[clamp(2rem,3.4vw,3.5rem)] font-semibold leading-[0.98] tracking-[-0.05em] text-[#0a1218]">
                      Get 25% off your
                      <br className="hidden sm:block" />
                      {" "}first order.
                    </h2>

                    <p className="mt-5 max-w-[500px] text-sm leading-6 text-[#56636a] sm:text-[15px]">
                      Save 25% on your first order.
                    </p>

                    <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-[9px] font-semibold uppercase tracking-[0.15em] text-[#718087]">
                      <span>Regular-Priced Items</span>

                      <span aria-hidden="true" className="size-1 rounded-full bg-[#159fdf]" />

                      <span>One Use Per Customer</span>
                    </div>
                  </div>

                  {/* RIGHT — COUPON */}
                  <div className="w-full shrink-0 lg:w-auto">
                    <p className="mb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#718087] lg:text-right">
                      Enter at checkout
                    </p>

                    <div className="group relative">
                      <div aria-hidden="true" className="absolute -inset-3 rounded-[22px] bg-[#159fdf]/[0.07] blur-2xl transition-opacity duration-300 group-hover:opacity-100" />

                      <div className="relative flex min-h-[70px] overflow-hidden rounded-[18px] border border-[#c8dce5] bg-white/80 shadow-[0_12px_40px_rgba(16,70,94,0.06)] backdrop-blur-md transition-all duration-300 group-hover:border-[#159fdf]/50">
                        <div className="flex flex-1 items-center px-6 py-4 sm:px-8">
                          <span className="text-xl font-semibold tracking-[0.15em] text-[#0a1218] sm:text-2xl">
                            WELCOME25
                          </span>
                        </div>

                        <div className="flex items-center border-l border-[#dbe6eb] bg-[#087db3] px-5 sm:px-6">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                            25% OFF
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FINE PRINT */}
                <div className="relative z-10 border-t border-[#dbe6eb] px-7 py-4 sm:px-10 lg:px-14">
                  <p className="text-[9px] uppercase tracking-[0.14em] text-[#87969d]">
                    Promotional offer subject to eligibility and checkout
                    validation.
                  </p>
                </div>
              </div>
            </div>
          </section>}

        {/* PRODUCT CATALOG */}
        <div id="shop-catalog">
          <Catalog />
        </div>

        {/* CUSTOM PACK PROMOTIONAL BANNER */}
        {accessGranted && <PackPromo />}
      </main>

      <Footer />
    </div>;
}